import {
  CURRENT_COOKIE_POLICY_VERSION,
  CURRENT_PRIVACY_VERSION,
  CURRENT_TERMS_VERSION,
} from './legalVersions';

/** Mirrors the legal.txt UserConsentLog / enterprise checklist. */
export type ConsentAuditEntry = {
  id: string;
  user_id?: string;
  email?: string;
  terms_version: string;
  privacy_version: string;
  cookie_policy_version?: string;
  accepted_at: string;
  ip_address: string;
  user_agent: string;
  event: 'signup' | 'terms_reaccept' | 'cookie_preferences' | 'login_ack';
};

const STORAGE_KEY = 'coremonit_consent_audit_v1';
const ACCEPTED_KEY = 'coremonit_accepted_legal_versions_v1';
const COOKIE_PREFS_KEY = 'coremonit_cookie_preferences_v1';

function readLogs(): ConsentAuditEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ConsentAuditEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** Append-only consent log (client-side audit trail; sync to backend in production). */
export function appendConsentLog(entry: Omit<ConsentAuditEntry, 'id' | 'accepted_at'> & { accepted_at?: string }): ConsentAuditEntry {
  const full: ConsentAuditEntry = {
    ...entry,
    id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `consent_${Date.now()}`,
    accepted_at: entry.accepted_at ?? new Date().toISOString(),
  };
  const next = [...readLogs(), full];
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  return full;
}

export function getConsentLogs(): readonly ConsentAuditEntry[] {
  return readLogs();
}

export type AcceptedLegalVersions = {
  termsVersion: string;
  privacyVersion: string;
  acceptedAt: string;
};

export function getAcceptedLegalVersions(): AcceptedLegalVersions | null {
  try {
    const raw = localStorage.getItem(ACCEPTED_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as AcceptedLegalVersions;
    if (!v?.termsVersion || !v?.privacyVersion) return null;
    return v;
  } catch {
    return null;
  }
}

export function setAcceptedLegalVersions(termsVersion: string, privacyVersion: string): void {
  const payload: AcceptedLegalVersions = {
    termsVersion,
    privacyVersion,
    acceptedAt: new Date().toISOString(),
  };
  localStorage.setItem(ACCEPTED_KEY, JSON.stringify(payload));
}

/** True when stored acceptance is older than the published policy versions. */
export function needsTermsOrPrivacyReacceptance(): boolean {
  const cur = getAcceptedLegalVersions();
  if (!cur) return true;
  return cur.termsVersion !== CURRENT_TERMS_VERSION || cur.privacyVersion !== CURRENT_PRIVACY_VERSION;
}

export type CookiePreferences = {
  essential: boolean;
  analytics: boolean;
  version: string;
  updatedAt: string;
};

export function getCookiePreferences(): CookiePreferences | null {
  try {
    const raw = localStorage.getItem(COOKIE_PREFS_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CookiePreferences;
  } catch {
    return null;
  }
}

export function setCookiePreferences(prefs: Omit<CookiePreferences, 'updatedAt'>): void {
  const full: CookiePreferences = {
    ...prefs,
    updatedAt: new Date().toISOString(),
  };
  localStorage.setItem(COOKIE_PREFS_KEY, JSON.stringify(full));
}

export function hasCookieBannerBeenResolved(): boolean {
  return getCookiePreferences() !== null;
}
